import { compareAsc, subDays } from 'date-fns'
import { createSelector } from 'reselect'
import { IntegrationStatus, LicenseType } from '../../../models/License'
import { getRooms } from '../../bookingCalendar/selectors/erhaltenZimmer'
import { IStore } from '../../index'
import { getBookingObjects } from '../../booking/selectors'
import { getBoxBuyStatus } from '../../boxbuy/selectors'

export function checkDate(date) {
  const d = date || date !== '0' ? date : subDays(new Date(), 1)

  return compareAsc(new Date(d), new Date()) > 0
}

export const getLicenses = (state: IStore) => state.userAccount.license.items

export const newLicences = [
  LicenseType.LICENSE_PREMIUM_ASSISTENT,
  LicenseType.LICENSE_PREMIUM_BASIC,
  LicenseType.LICENSE_PREMIUM_ROOMS,
  LicenseType.LICENSE_PREMIUM_PROFESSIONAL,
  LicenseType.LICENSE_PREMIUM_LITE,
  LicenseType.LICENSE_PREMIUM_PLUS,
  LicenseType.LICENSE_PREMIUM_PRO,
  LicenseType.LICENSE_PREMIUM_MAX,
]

export const getLicense = (type: LicenseType) =>
  createSelector(
    [getLicenses],
    licenses => {
      let license = licenses.slice().sort((a,b) => b.active - a.active).find(license => license.licenseType === type)
      if (type === LicenseType.LICENSE_INVOICE) {
        license = {
          ...license,
          link: (
            licenses.find(l => l.licenseType === type && l.link) || {
              link: undefined,
            }
          ).link,
        }
      }

      return license
    },
  )

export const getLicenseLast = (type: LicenseType) =>
  createSelector(
    [getLicenses],
    licenses => {
      let license = licenses
        .reverse()
        .find(license => license.licenseType === type)

      if (type === LicenseType.LICENSE_INVOICE) {
        license = {
          ...license,
          link: (
            licenses.find(l => l.licenseType === type && l.link) || {
              link: undefined,
            }
          ).link,
        }
      }

      return license
    },
  )

export const getInvoiceLicense = createSelector(
  [getLicenses],
  licenses =>
    licenses.find(
      license =>
        license.licenseType === LicenseType.LICENSE_INVOICE && license.link,
    ),
)

export const isAllInOneLicenseValid = createSelector(
  [getLicense(LicenseType.LICENSE_PREMIUM_ROOMS)],
  license => {
    return checkDate(license ? license.license : 0)
  },
)

export const isNewPremiumLicenseValid = createSelector(
  [
    getLicense(LicenseType.LICENSE_PREMIUM_LITE),
    getLicense(LicenseType.LICENSE_PREMIUM_PLUS),
    getLicense(LicenseType.LICENSE_PREMIUM_PRO),
    getLicense(LicenseType.LICENSE_PREMIUM_MAX),
  ],
  (
    premiumlite,
    premiumPlus,
    premiumPro,
    premiumMax,
  ) => {
    return (
      (premiumlite &&
        checkDate(premiumlite.rawDate || premiumlite.license)) ||
      (premiumPlus &&
        checkDate(premiumPlus.rawDate || premiumPlus.license)) ||
      (premiumPro &&
        checkDate(premiumPro.rawDate || premiumPro.license)) ||
      (premiumMax &&
        checkDate(premiumMax.rawDate || premiumMax.license))
    )
  },
)

export const isLicenseValid = createSelector(
  [
    getLicense(LicenseType.LICENSE_WEB_ANDROID),
    getLicense(LicenseType.LICENSE_WEB_ANDROID_IOS),
    getLicense(LicenseType.LICENSE_WEB_IOS),
    getLicense(LicenseType.LICENSE_PREMIUM_ROOMS),
    getLicense(LicenseType.LICENSE_PREMIUM_BASIC),
    getLicense(LicenseType.LICENSE_PREMIUM_ASSISTENT),
    getLicense(LicenseType.LICENSE_PREMIUM_PROFESSIONAL),
    getLicense(LicenseType.LICENSE_PREMIUM_LITE),
    getLicense(LicenseType.LICENSE_PREMIUM_PLUS),
    getLicense(LicenseType.LICENSE_PREMIUM_PRO),
    getLicense(LicenseType.LICENSE_PREMIUM_MAX),
  ],
  (
    android,
    full,
    ios,
    premiumRooms,
    premiumBasic,
    premiumAssistent,
    premiumProfessional,
    premiumlite,
    premiumPlus,
    premiumPro,
    premiumMax,
  ) => {
    return (
      (android && checkDate(android.rawDate || android.license)) ||
      (full && checkDate(full.rawDate || full.license)) ||
      (ios && checkDate(ios.rawDate || ios.license)) ||
      (premiumRooms &&
        checkDate(premiumRooms.rawDate || premiumRooms.license)) ||
      (premiumBasic &&
        checkDate(premiumBasic.rawDate || premiumBasic.license)) ||
      (premiumAssistent &&
        checkDate(premiumAssistent.rawDate || premiumAssistent.license)) ||
      (premiumProfessional &&
        checkDate(premiumProfessional.rawDate || premiumProfessional.license)) ||
      (premiumlite &&
        checkDate(premiumlite.rawDate || premiumlite.license)) ||
      (premiumPlus &&
        checkDate(premiumPlus.rawDate || premiumPlus.license)) ||
      (premiumPro &&
        checkDate(premiumPro.rawDate || premiumPro.license)) ||
      (premiumMax &&
        checkDate(premiumMax.rawDate || premiumMax.license))
    )
  },
)

export const isValidLicense = (type: LicenseType) =>
  createSelector(
    [getLicense(type)],
    license => {
      return checkDate(license ? license.rawDate || license.license : 0)
    },
  )

export const validLicenses = createSelector(
  [getLicenses],
  licenses =>
    licenses.filter(license => checkDate(license.rawDate || license.license)),
)

export const newValidLicenses = createSelector(
  [
    getLicense(LicenseType.LICENSE_PREMIUM_ASSISTENT),
    getLicense(LicenseType.LICENSE_PREMIUM_BASIC),
    getLicense(LicenseType.LICENSE_PREMIUM_ROOMS),
    getLicense(LicenseType.LICENSE_PREMIUM_PROFESSIONAL),
    getLicenseLast(LicenseType.LICENSE_ICALENDAR),
    getLicense(LicenseType.LICENSE_PREMIUM_LITE),
    getLicense(LicenseType.LICENSE_PREMIUM_PLUS),
    getLicense(LicenseType.LICENSE_PREMIUM_PRO),
    getLicense(LicenseType.LICENSE_PREMIUM_MAX),

  ],
  (ass, basic, rooms, prof, ical, lite,plus,pro,max) => {
    return [ass, basic, rooms, prof, ical, lite, plus,pro,max]
      .filter(Boolean)
      .sort(
        (a, b) => new Date(a.license).getTime() - new Date(b.license).getTime(),
      )
  },
)

export const getActiveLicense = createSelector([newValidLicenses], licenses => {
  return licenses.find(l => {
    return Number(l.active) === 1})
})


export const isTrialAccount = createSelector([validLicenses], licenses => {
  return licenses.some(l => l.active === 1 && (l.source === 'api_freemium' || l.source === 'website_freemium'))
})

export const newValidLicensesBooking = createSelector(
  [
    getLicense(LicenseType.LICENSE_PREMIUM_ASSISTENT),
    getLicense(LicenseType.LICENSE_PREMIUM_BASIC),
    getLicense(LicenseType.LICENSE_PREMIUM_ROOMS),
    getLicense(LicenseType.LICENSE_PREMIUM_LITE),
    getLicense(LicenseType.LICENSE_PREMIUM_PLUS),
    getLicense(LicenseType.LICENSE_PREMIUM_PRO),
    getLicense(LicenseType.LICENSE_PREMIUM_MAX),
    getLicenseLast(LicenseType.LICENSE_BOOKING_COM),
  ],
  (ass, basic, rooms, prof, lite, pro, plus,max, booking) => {
    return [ass, basic, rooms, prof, booking, lite, pro, plus, max]
      .filter(Boolean)
      .sort(
        (a, b) => new Date(a.license).getTime() - new Date(b.license).getTime(),
      )
  },
)

export const getNewLicences = createSelector(
  [getLicenses],
  licenses => licenses.filter(l => newLicences.includes(l.licenseType)),
)
export const isNewLicencesValid = createSelector(
  [getNewLicences],
  licences => licences.filter(license => checkDate(license.license)),
)
export function isLicenseLoading(state: IStore) {
  return state.userAccount.license.isLoading
}

export const integrations = createSelector(
  [
    getLicense(LicenseType.LICENSE_INVOICE),
    getLicense(LicenseType.LICENSE_BOOKING_COM),
    getLicense(LicenseType.LICENSE_ICALENDAR),
  ],
  (invoice, booking, iCalendar) => {
    return {
      booking,
      iCalendar,
      invoice,
    }
  },
)

export const getIntegrations = createSelector(
  [getRooms, integrations, getBookingObjects, getBoxBuyStatus],
  (rooms, services, objectsb, boxBuyStatus) => {
    if (services && services.booking && services.iCalendar) {
      const iCalendar = rooms ? rooms.find(room => room.iCalendar) : 0
      const bookingActive =
        services.booking.license !== null &&
        compareAsc(services.booking.license, new Date()) > -1
      const iCalendarActive =
        services.iCalendar.license !== null &&
        compareAsc(services.iCalendar.license, new Date()) > -1

      const everyConfigured =
        objectsb && objectsb.every(obj => obj.is_sync === '1')

      let bookingStatus
      if (bookingActive && !everyConfigured) {
        bookingStatus = IntegrationStatus.Inactive
      } else if (bookingActive && everyConfigured && objectsb.length > 0) {
        bookingStatus = IntegrationStatus.Active
      } else if (bookingActive && objectsb.length === 0) {
        bookingStatus = IntegrationStatus.NotConfigured
      } else {
        bookingStatus = IntegrationStatus.NotPurchased
      }

      let iCalendarStatus
      if (iCalendarActive && !services.iCalendar.status && iCalendar) {
        iCalendarStatus = IntegrationStatus.Inactive
      } else if (iCalendarActive && services.iCalendar.status && iCalendar) {
        iCalendarStatus = IntegrationStatus.Active
      } else if (iCalendarActive && !iCalendar) {
        iCalendarStatus = IntegrationStatus.NotConfigured
      } else {
        iCalendarStatus = IntegrationStatus.NotPurchased
      }

      return {
        ...services,
        booking: {
          ...services.booking,
          status: bookingStatus,
        },
        iCalendar: {
          ...services.iCalendar,
          status: iCalendarStatus,
        },
        airbnb: {
          status: IntegrationStatus.Inactive,
        },
        boxBuy: {
          status: boxBuyStatus
        }
      }
    }
    return {}
  },
) as any
